import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Loader2, Sparkles, Check, AlertCircle, Calendar, RefreshCcw, MapPin, Clock } from 'lucide-react';
import type { Email } from '@/types/email';
import { Button } from '@/components/ui/button';
import { CreationPopover } from './CreationPopover';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMessageEventSuggestions, type EventSuggestionEntry } from '@/hooks/useMessageEventSuggestions';
import { useCalendarStore } from '@/stores/calendarStore';
import { useCalendarActionsStore } from '@/stores/calendarActionsStore';
import { cn } from '@/lib/utils';
import type { CalendarListEntry } from '@/types/calendar';

interface EmailEventCreationContextValue {
  email: Email;
  eventSuggestions: ReturnType<typeof useMessageEventSuggestions>;
  openPopover: (
    anchor: HTMLElement | { getBoundingClientRect: () => DOMRect; contextElement?: Element },
    options?: PopoverPositioningOptions
  ) => void;
  closePopover: () => void;
  calendarReady: boolean;
  calendarLoading: boolean;
  popoverState: {
    open: boolean;
    align: 'start' | 'center' | 'end';
    side: 'top' | 'bottom' | 'left' | 'right';
    width: number | null;
    anchorRef: React.RefObject<{ getBoundingClientRect: () => DOMRect; contextElement?: Element } | null>;
  };
}

type PopoverPositioningOptions = {
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  width?: number;
};

const EmailEventCreationContext = createContext<EmailEventCreationContextValue | null>(null);

export function EmailEventCreationProvider({
  email,
  children,
}: {
  email: Email;
  children: ReactNode;
}) {
  const eventSuggestions = useMessageEventSuggestions(email);
  const [anchorState, setAnchorState] = useState<{
    element: HTMLElement | { getBoundingClientRect: () => DOMRect; contextElement?: Element } | null;
    key: number;
  }>({
    element: null,
    key: 0,
  });
  const anchor = anchorState.element;
  const [align, setAlign] = useState<'start' | 'center' | 'end'>('end');
  const [side, setSide] = useState<'top' | 'bottom' | 'left' | 'right'>('bottom');
  const [width, setWidth] = useState<number | null>(640);
  const anchorRef = useRef<{ getBoundingClientRect: () => DOMRect; contextElement?: Element } | null>(null);

  useEffect(() => {
    anchorRef.current = anchor
      ? {
          getBoundingClientRect: () => anchor.getBoundingClientRect(),
          contextElement: anchor instanceof HTMLElement ? anchor : anchor.contextElement,
        }
      : null;
  }, [anchorState.key, anchor]);
  const autoRequestRef = useRef(false);
  const calendarInitialized = useCalendarStore((state) => state.initialized);
  const calendarLoading = useCalendarStore((state) => state.loading);
  const bootstrapCalendar = useCalendarActionsStore((state) => state.bootstrap);

  useEffect(() => {
    if (!anchor) {
      autoRequestRef.current = false;
    }
  }, [anchorState.key, anchor]);

  useEffect(() => {
    if (!anchor) {
      return;
    }
    if (calendarInitialized || calendarLoading) {
      return;
    }
    void bootstrapCalendar();
  }, [anchorState.key, anchor, calendarInitialized, calendarLoading, bootstrapCalendar]);

  const closePopover = useCallback(() => {
    setAnchorState((prev) => ({ ...prev, element: null }));
  }, []);

  const { annotation: suggestionAnnotation, suggestions, isLoading: suggestionsLoading, requestSuggestions } =
    eventSuggestions;
  const suggestionsLength = suggestions.length;

  const openPopover = useCallback(
    (target: HTMLElement | { getBoundingClientRect: () => DOMRect; contextElement?: Element }, options?: PopoverPositioningOptions) => {
      setAlign(options?.align ?? 'end');
      setSide(options?.side ?? 'bottom');
      setWidth(options?.width ?? 640);
      const element =
        'getBoundingClientRect' in target ? target : (target as HTMLElement);
      setAnchorState({ element, key: Date.now() });

      const hasSuggestionData =
        (suggestionAnnotation && suggestionsLength > 0) || suggestionsLength > 0;
      if (!hasSuggestionData && !suggestionsLoading && !autoRequestRef.current) {
        autoRequestRef.current = true;
        void requestSuggestions();
      }
    },
    [suggestionAnnotation, suggestionsLength, suggestionsLoading, requestSuggestions]
  );

  useEffect(() => {
    if (!anchor) {
      return;
    }
    if (suggestionsLoading) {
      return;
    }
    const hasSuggestionData = (suggestionAnnotation && suggestionsLength > 0) || suggestionsLength > 0;
    if (hasSuggestionData) {
      autoRequestRef.current = true;
      return;
    }
    if (autoRequestRef.current) {
      return;
    }
    autoRequestRef.current = true;
    void requestSuggestions();
  }, [anchorState.key, anchor, suggestionAnnotation, suggestionsLength, suggestionsLoading, requestSuggestions]);

  const contextValue = useMemo<EmailEventCreationContextValue>(
    () => ({
      email,
      eventSuggestions,
      openPopover,
      closePopover,
      calendarReady: calendarInitialized,
      calendarLoading,
      popoverState: {
        open: Boolean(anchor),
        align,
        side,
        width,
        anchorRef,
      },
    }),
    [
      email,
      eventSuggestions,
      openPopover,
      closePopover,
      anchor,
      align,
      side,
      width,
      anchorRef,
      calendarInitialized,
      calendarLoading,
    ]
  );

  return (
    <EmailEventCreationContext.Provider value={contextValue}>
      {children}
      <EmailEventCreationPopover />
    </EmailEventCreationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEmailEventCreation() {
  const ctx = useContext(EmailEventCreationContext);
  if (!ctx) {
    throw new Error('EmailEventCreationContext unavailable');
  }
  return ctx;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalEmailEventCreation() {
  return useContext(EmailEventCreationContext);
}

function formatEventDateTime(suggestion: EventSuggestionEntry): string {
  const { startDate, startTime, endDate, endTime, allDay } = suggestion;
  const startDateFormatted = new Date(`${startDate}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  if (allDay) {
    if (endDate && endDate !== startDate) {
      const endDateFormatted = new Date(`${endDate}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
      return `${startDateFormatted} - ${endDateFormatted} (all day)`;
    }
    return `${startDateFormatted} (all day)`;
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  };

  let result = startDateFormatted;
  if (startTime) {
    result += ` ${formatTime(startTime)}`;
  }
  if (endTime && endTime !== startTime) {
    result += ` - ${formatTime(endTime)}`;
  }
  return result;
}

function EditableEventCard({
  suggestion,
  created,
  creating,
  error,
  onCreate,
  disabled = false,
  disabledReason,
  calendarOptions,
  defaultCalendarUid,
}: {
  suggestion: EventSuggestionEntry;
  created: boolean;
  creating: boolean;
  error: string | null;
  onCreate: (payload: {
    title: string;
    calendarUid: string;
    startDate: string;
    startTime: string | null;
    endDate: string | null;
    endTime: string | null;
    location: string | null;
    description: string;
    allDay: boolean;
  }) => Promise<void>;
  disabled?: boolean;
  disabledReason?: string | null;
  calendarOptions: CalendarListEntry[];
  defaultCalendarUid: string | null;
}) {
  const [title, setTitle] = useState(suggestion.title);
  const [calendarUid, setCalendarUid] = useState<string | null>(defaultCalendarUid);
  const isActionDisabled = creating || created || disabled || !calendarOptions.length;

  useEffect(() => {
    setTitle(suggestion.title);
    if (!calendarUid && defaultCalendarUid) {
      setCalendarUid(defaultCalendarUid);
    }
  }, [suggestion.id, suggestion.title, defaultCalendarUid, calendarUid]);

  const handleCreate = async () => {
    if (!calendarUid) {
      return;
    }
    await onCreate({
      title: title.trim() || suggestion.title,
      calendarUid,
      startDate: suggestion.startDate,
      startTime: suggestion.startTime,
      endDate: suggestion.endDate,
      endTime: suggestion.endTime,
      location: suggestion.location,
      description: suggestion.description,
      allDay: suggestion.allDay,
    });
  };

  return (
    <div className="rounded-2xl border border-border/60 bg-card/80 p-4 space-y-3">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-4 w-4" />
          </span>
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Event title"
            className="text-sm font-medium flex-1 min-w-[180px]"
            disabled={isActionDisabled}
          />
          {calendarOptions.length > 0 && (
            <Select
              value={calendarUid ?? ''}
              onValueChange={setCalendarUid}
              disabled={isActionDisabled}
            >
              <SelectTrigger className="w-[200px] h-9">
                <SelectValue placeholder="Choose calendar" />
              </SelectTrigger>
              <SelectContent onEscapeKeyDown={(e) => e.stopPropagation()}>
                {calendarOptions.map((calendar) => (
                  <SelectItem key={calendar.uid} value={calendar.uid}>
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: calendar.backgroundColor ?? '#94a3b8' }}
                      />
                      {calendar.summary}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={isActionDisabled || !calendarUid}
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : created ? (
              <Check className="h-4 w-4" />
            ) : (
              'Add'
            )}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground ml-10">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatEventDateTime(suggestion)}
          </span>
          {suggestion.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {suggestion.location}
            </span>
          )}
        </div>

        {disabled && disabledReason ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1 ml-10">
            <Loader2 className="h-3 w-3 animate-spin" />
            {disabledReason}
          </p>
        ) : error ? (
          <p className="text-xs text-destructive flex items-center gap-1 ml-10">
            <AlertCircle className="h-3 w-3" />
            {error}
          </p>
        ) : !calendarOptions.length ? (
          <p className="text-xs text-muted-foreground ml-10">
            Connect a calendar account to add events
          </p>
        ) : null}
      </div>

      {suggestion.description && (
        <p className="text-xs text-muted-foreground ml-10 line-clamp-2">
          {suggestion.description}
        </p>
      )}
    </div>
  );
}

function EmailEventCreationPopover() {
  const ctx = useContext(EmailEventCreationContext);
  const createEvent = useCalendarActionsStore((state) => state.createEvent);
  const calendars = useCalendarStore((state) => state.calendars);
  const calendarOrder = useCalendarStore((state) => state.calendarOrder);
  const timezone = useCalendarStore((state) => state.timezone) ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [created, setCreated] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [initialEmailId, setInitialEmailId] = useState<string | null>(null);

  if (!ctx) {
    throw new Error('EmailEventCreationContext unavailable');
  }

  const { email, eventSuggestions, popoverState, closePopover, calendarReady, calendarLoading } = ctx;

  const calendarOptions = useMemo(() => {
    return calendarOrder
      .map((uid) => calendars[uid])
      .filter((calendar): calendar is CalendarListEntry => Boolean(calendar) && calendar.canEdit !== false)
      .reverse();
  }, [calendars, calendarOrder]);

  const defaultCalendarUid = useMemo(() => {
    return calendarOptions[0]?.uid ?? null;
  }, [calendarOptions]);

  useEffect(() => {
    if (initialEmailId !== email.id) {
      setInitialEmailId(email.id);
      setCreated(new Set());
      setErrors({});
    }
  }, [email.id, initialEmailId]);

  const handleCreate = useCallback(
    async (suggestion: EventSuggestionEntry, overrides: {
      title: string;
      calendarUid: string;
      startDate: string;
      startTime: string | null;
      endDate: string | null;
      endTime: string | null;
      location: string | null;
      description: string;
      allDay: boolean;
    }) => {
      const suggestionId = `ai-${suggestion.id}`;
      setErrors((prev) => ({ ...prev, [suggestionId]: null }));
      setPending((prev) => {
        const next = new Set(prev);
        next.add(suggestionId);
        return next;
      });

      try {
        const { startDate, startTime, endDate, endTime, allDay } = overrides;
        const effectiveEndDate = endDate || startDate;

        // For all-day events, Google Calendar requires the end date to be exclusive (next day)
        const exclusiveEndDate = allDay
          ? new Date(new Date(`${effectiveEndDate}T00:00:00`).getTime() + 86400000).toISOString().slice(0, 10)
          : effectiveEndDate;

        const event = {
          summary: overrides.title,
          location: overrides.location ?? undefined,
          description: overrides.description || undefined,
          allDay,
          start: allDay
            ? { date: startDate, dateTime: null, timeZone: timezone }
            : { date: null, dateTime: `${startDate}T${startTime || '09:00'}:00`, timeZone: timezone },
          end: allDay
            ? { date: exclusiveEndDate, dateTime: null, timeZone: timezone }
            : { date: null, dateTime: `${effectiveEndDate}T${endTime || startTime || '10:00'}:00`, timeZone: timezone },
        };

        const result = await createEvent({
          calendarUid: overrides.calendarUid,
          event,
          sendUpdates: 'none',
        });

        if (result) {
          setCreated((prev) => {
            const next = new Set(prev);
            next.add(suggestionId);
            return next;
          });
        } else {
          throw new Error('Failed to create event');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to create event';
        setErrors((prev) => ({ ...prev, [suggestionId]: message }));
      } finally {
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(suggestionId);
          return next;
        });
      }
    },
    [createEvent, timezone]
  );

  const allSuggestions = eventSuggestions.suggestions;

  return (
    <CreationPopover
      open={popoverState.open}
      onClose={closePopover}
      title="Add to calendar"
      headerActions={
        <Button
          size="sm"
          variant="secondary"
          className="gap-2"
          onClick={() => eventSuggestions.requestSuggestions({ force: true })}
          disabled={eventSuggestions.isLoading}
        >
          <RefreshCcw className={cn('h-4 w-4', eventSuggestions.isLoading && 'animate-spin')} />
          {eventSuggestions.isLoading ? 'Working...' : 'Refresh'}
        </Button>
      }
      anchorRef={popoverState.anchorRef}
      align={popoverState.align}
      side={popoverState.side}
      desktopStyle={popoverState.width ? { width: popoverState.width } : undefined}
    >
      <div className="space-y-3">
              {eventSuggestions.error ? (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {eventSuggestions.error}
                </p>
              ) : null}
              {eventSuggestions.isLoading && !allSuggestions.length ? (
                <div className="flex h-24 items-center justify-center text-sm text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Scanning for events...
                </div>
              ) : null}
              {!allSuggestions.length && !eventSuggestions.isLoading ? (
                <p className="text-xs text-muted-foreground">No calendar events found in this email.</p>
              ) : null}
              {allSuggestions.map((suggestion) => {
                const suggestionId = `ai-${suggestion.id}`;
                return (
                  <EditableEventCard
                    key={suggestionId}
                    suggestion={suggestion}
                    created={created.has(suggestionId)}
                    creating={pending.has(suggestionId)}
                    error={errors[suggestionId] ?? null}
                    disabled={!calendarReady}
                    disabledReason={
                      !calendarReady
                        ? calendarLoading
                          ? 'Loading calendars...'
                          : 'Finish loading calendars before adding'
                        : null
                    }
                    calendarOptions={calendarOptions}
                    defaultCalendarUid={defaultCalendarUid}
                    onCreate={(payload) => handleCreate(suggestion, payload)}
                  />
                );
              })}
      </div>
    </CreationPopover>
  );
}

export function EmailEventCreationIconButton({
  side = 'bottom',
  align = 'end',
  className,
  variant = 'ghost',
  size = 'icon',
}: {
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  className?: string;
  variant?: 'outline' | 'ghost';
  size?: 'icon' | 'sm';
}) {
  const ctx = useOptionalEmailEventCreation();
  if (!ctx) {
    return null;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          onClick={(event) => ctx.openPopover(event.currentTarget, { align, side })}
        >
          <Calendar className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Add to calendar</TooltipContent>
    </Tooltip>
  );
}
